import { useQuery } from '@tanstack/react-query'
import { api } from '../../../api/client'
import { formatDateTime } from '../../../lib/format'

interface CollectorPause {
	collector: string
	reason: string
	pausedAt: string
	storefront: string | null
	retryAt: string | null
	retryStatus: 'automatic' | 'disabled' | 'manual'
}

/** Active errors stay visible independently of the crash report filters. */
export function CollectorPausesPanel() {
	const pauses = useQuery({
		queryKey: ['admin-collector-pauses'],
		queryFn: () => api.get<{ pauses: CollectorPause[] }>('/api/admin/collector-pauses'),
		refetchInterval: 30_000,
	})
	return (
		<section className="card stack" aria-label="Collector errors">
			<h2 style={{ margin: 0 }}>Collector errors</h2>
			{pauses.isPending && <p className="muted">Checking for paused collectors…</p>}
			{pauses.isError && <p className="error">Could not check collector pauses: {pauses.error.message}</p>}
			{pauses.data?.pauses.length === 0 && <p className="muted">No collectors are paused.</p>}
			{pauses.data?.pauses.map((pause) => (
				<div key={pause.collector} role="status" className="stack">
					<h3 className="error" style={{ margin: 0 }}>
						{pause.collector} collector paused
					</h3>
					<p style={{ margin: 0, overflowWrap: 'anywhere' }}>
						{pause.storefront && `${pause.storefront}: `}
						{pause.reason}
					</p>
					<p className="small muted" style={{ margin: 0 }}>
						Paused {formatDateTime(pause.pausedAt)}
					</p>
					<p style={{ margin: 0 }}>
						{pause.retryAt
							? new Date(pause.retryAt).getTime() <= Date.now()
								? 'Retry is due; waiting for the scheduler.'
								: `Automatic retry at ${formatDateTime(pause.retryAt)}.`
							: pause.retryStatus === 'disabled'
								? 'Automatic retry is disabled while collection is turned off.'
								: 'An administrator must resume this collector after the error is fixed.'}
					</p>
				</div>
			))}
		</section>
	)
}
